import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { toDataURL } from 'qrcode';
import { ConfirmEditModalComponent, DateFieldComponent } from '@clinic-platform/ui-components';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { AuthService } from '../../../core/auth/auth.service';
import { UserRole } from '../../../core/auth/user-role.type';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import { LoadingBlockComponent } from '../../../shared/ui/loading-block/loading-block.component';
import { EmptyStateComponent } from '../../../shared/ui/empty-state/empty-state.component';
import {
  ClientAdminItem,
  ClientCommunicationChannels,
} from '../../clients/data-access/clients-admin.models';
import {
  ClientCommunicationSummaryView,
  presentClientCommunicationChannels,
} from '../../clients/data-access/client-communication.presenter';
import { ClientsAdminService } from '../../clients/data-access/clients-admin.service';
import { ProcedureTypeItem } from '../../procedure-types/data-access/procedure-types.models';
import { ProcedureTypesService } from '../../procedure-types/data-access/procedure-types.service';
import { SettingsService } from '../../settings/data-access/settings.service';
import { TelegramConnectorService } from '../../telegram/data-access/telegram-connector.service';
import {
  TelegramLinkDto,
  TelegramLinkTokenDto,
} from '../../telegram/data-access/telegram-connector.models';
import {
  ClinicOperationalSettingsDto,
  CompanyRatingSettingsDto,
} from '../../settings/data-access/settings.models';
import {
  ManualAdjustUserClientRatingRequest,
  UpdateUserRequest,
  UserClientRatingEvent,
  UserDetailItem,
  UserItem
} from '../data-access/users.models';
import { UsersService } from '../data-access/users.service';

type EntityPresentationMode = 'quick-view' | 'full-view';

@Component({
  selector: 'app-user-profile-page',
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    ConfirmEditModalComponent,
    DateFieldComponent,
    TranslatePipe,
    PageHeaderComponent,
    LoadingBlockComponent,
    EmptyStateComponent
  ],
  template: `
    @if (!isQuickView) {
      <div class="user-profile-page__head">
        <app-page-header
          title="User Profile"
          subtitle="Detailed user view and edit actions"
        />

        <button type="button" class="user-profile-page__back" (click)="goBack()">
          {{ 'Back to Users' | t }}
        </button>
      </div>
    }

    @if (isLoading) {
      <app-loading-block text="Loading user..." />
    } @else if (errorText) {
      <app-empty-state
        title="Failed to load user"
        [description]="errorText"
      />
    } @else if (!item) {
      <app-empty-state
        title="User not found"
        description="No user data available for this id."
      />
    } @else {
      @if (actionErrorText) {
        <div class="user-profile-page__error">{{ actionErrorText | t }}</div>
      }

      @if (successText) {
        <div class="user-profile-page__success">{{ successText }}</div>
      }

      <section class="user-profile-page__card">
        <div class="user-profile-page__row user-profile-page__row--wide">
          <span>Profile Photo</span>
          <div class="user-profile-page__photo-block">
            @if (item.photoUrl) {
              <img
                [src]="item.photoUrl"
                alt="User profile photo"
                class="user-profile-page__photo-image"
              />
            } @else {
              <div class="user-profile-page__photo-placeholder">No photo uploaded</div>
            }

            @if (canEdit && !isQuickView) {
              <div class="user-profile-page__photo-controls">
                <input
                  type="file"
                  accept="image/*"
                  [disabled]="isPhotoUploading || isPhotoRemoving"
                  (change)="onPhotoSelected($event)"
                />

                @if (selectedPhotoName) {
                  <div class="user-profile-page__account-meta">
                    Selected: {{ selectedPhotoName }}
                  </div>
                }

                <div class="user-profile-page__client-actions-row">
                  <button
                    type="button"
                    class="user-profile-page__action user-profile-page__action--primary"
                    [disabled]="!selectedPhotoFile || isPhotoUploading || isPhotoRemoving"
                    (click)="uploadPhoto()"
                  >
                    {{ isPhotoUploading ? 'Uploading...' : 'Upload photo' }}
                  </button>

                  @if (item.photoUrl) {
                    <button
                      type="button"
                      class="user-profile-page__action"
                      [disabled]="isPhotoUploading || isPhotoRemoving"
                      (click)="removePhoto()"
                    >
                      {{ isPhotoRemoving ? 'Removing...' : 'Remove photo' }}
                    </button>
                  }
                </div>
              </div>
            }
          </div>
        </div>

        <div class="user-profile-page__row">
          <span>Email</span>
          <strong>{{ item.email }}</strong>
        </div>
        <div class="user-profile-page__row">
          <span>Phone</span>
          <strong>{{ item.phone ?? '—' }}</strong>
        </div>
        <div class="user-profile-page__row">
          <span>Full Name</span>
          <strong>{{ item.fullName }}</strong>
        </div>
        <div class="user-profile-page__row">
          <span>Birth Date</span>
          <strong>{{ item.birthDate ?? '—' }}</strong>
        </div>
        <div class="user-profile-page__row">
          <span>Role</span>
          <strong>{{ ('role.' + item.role) | t }}</strong>
        </div>
        <div class="user-profile-page__row">
          <span>Status</span>
          <strong>
            @if (item.role === 'client') {
              {{ clientAccountStatusLabel | t }}
            } @else {
              {{ item.isActive ? ('Active' | t) : ('Inactive' | t) }}
            }
          </strong>
        </div>
        @if (item.role !== 'client') {
          <div class="user-profile-page__row">
            <span>{{ clinicLabel }}</span>
            <strong>{{ item.clinicId ? clinicDisplayName(item.clinicId) : '—' }}</strong>
          </div>
        }
        <div class="user-profile-page__row">
          <span>Created</span>
          <strong>{{ item.createdAt | date: 'dd.MM.yyyy HH:mm' }}</strong>
        </div>
        <div class="user-profile-page__row">
          <span>Updated</span>
          <strong>{{ item.updatedAt | date: 'dd.MM.yyyy HH:mm' }}</strong>
        </div>

        @if (item.role === 'specialist') {
          <div class="user-profile-page__row user-profile-page__row--wide">
            <span>Provided Service Types</span>
            <strong>
              @if (specialistProcedureTypeLabels.length === 0) {
                —
              } @else {
                {{ specialistProcedureTypeLabels.join(', ') }}
              }
            </strong>
          </div>
        }

        @if (item.role === 'client') {
          <div class="user-profile-page__row">
            <span>{{ 'clientReliability.title' | t }}</span>
            <div
              class="user-profile-page__client-score-pill"
              [class.user-profile-page__client-score-pill--good]="clientReliabilityTone === 'good'"
              [class.user-profile-page__client-score-pill--warn]="clientReliabilityTone === 'warn'"
              [class.user-profile-page__client-score-pill--risk]="clientReliabilityTone === 'risk'"
            >
              {{ clientReliabilityPercent }}% · {{ clientReliabilityLabel | t }}
            </div>
          </div>

          @if (clientCommunicationSummary; as communicationSummary) {
            <div class="user-profile-page__row user-profile-page__row--wide">
              <span>Communication channels</span>
              <div class="user-profile-page__communication">
                <div class="user-profile-page__communication-head">
                  <div>
                    <strong>{{ communicationSummary.label }}</strong>
                    <div class="user-profile-page__account-meta">{{ communicationSummary.meta }}</div>
                  </div>
                  <span
                    class="user-profile-page__communication-status"
                    [class.user-profile-page__communication-status--automatic]="communicationSummary.tone === 'automatic'"
                    [class.user-profile-page__communication-status--manual]="communicationSummary.tone === 'manual'"
                    [class.user-profile-page__communication-status--unreachable]="communicationSummary.tone === 'unreachable'"
                  >
                    {{ communicationSummary.label }}
                  </span>
                </div>

                <div class="user-profile-page__communication-grid">
                  @for (channel of communicationSummary.channels; track channel.id) {
                    <div
                      class="user-profile-page__communication-channel"
                      [class.user-profile-page__communication-channel--automatic]="channel.tone === 'automatic'"
                      [class.user-profile-page__communication-channel--manual]="channel.tone === 'manual'"
                      [class.user-profile-page__communication-channel--unreachable]="channel.tone === 'unreachable'"
                    >
                      <span>{{ channel.title }}</span>
                      <strong>{{ channel.value }}</strong>
                      <small>{{ channel.meta }}</small>
                    </div>
                  }
                </div>
              </div>
            </div>
          }

          <div class="user-profile-page__row user-profile-page__row--wide">
            <span>Notes</span>
            <strong>{{ item.clientProfile?.notes ?? '—' }}</strong>
          </div>

          <div class="user-profile-page__row user-profile-page__row--wide">
            <span>{{ 'clientReliability.historyTitle' | t }}</span>
            @if (isClientRatingHistoryLoading) {
              <div class="user-profile-page__account-meta">{{ 'Loading...' | t }}</div>
            } @else if (clientRatingHistory.length === 0) {
              <strong>—</strong>
            } @else {
              <div class="user-profile-page__rating-history">
                @for (event of clientRatingHistory; track event.id) {
                  <div class="user-profile-page__rating-history-item">
                    <div class="user-profile-page__rating-history-top">
                      <strong>{{ clientRatingEventLabel(event) | t }}</strong>
                      <span
                        class="user-profile-page__rating-delta"
                        [class.user-profile-page__rating-delta--positive]="event.deltaPercent > 0"
                        [class.user-profile-page__rating-delta--negative]="event.deltaPercent < 0"
                      >
                        {{ event.deltaPercent > 0 ? '+' : '' }}{{ event.deltaPercent }}%
                      </span>
                    </div>
                    <div class="user-profile-page__account-meta">
                      {{ event.createdAt | date: 'dd.MM.yyyy HH:mm' }} ·
                      {{ event.scoreBefore }}% → {{ event.scoreAfter }}%
                    </div>
                    @if (clientRatingEventMeta(event); as eventMeta) {
                      <div class="user-profile-page__account-meta">
                        {{ 'clientReliability.churnStep' | t }}: {{ eventMeta }}
                      </div>
                    }
                    @if (event.note) {
                      <div class="user-profile-page__account-meta">{{ event.note }}</div>
                    }
                  </div>
                }
              </div>
            }
          </div>

          @if (canManualAdjustClientReliability && !isQuickView) {
            <div class="user-profile-page__row user-profile-page__row--wide">
              <span>{{ 'clientReliability.manualAdjustmentTitle' | t }}</span>
              <div class="user-profile-page__client-actions">
                <label class="user-profile-page__client-rating-adjustment">
                  <span>{{ 'clientReliability.manualDelta' | t }}</span>
                  <input
                    type="number"
                    [value]="clientRatingDeltaDraft"
                    [disabled]="isClientRatingAdjusting"
                    (input)="onClientRatingDeltaChange($event)"
                  />
                </label>

                <label class="user-profile-page__client-email user-profile-page__client-email--wide">
                  <span>{{ 'clientReliability.manualReason' | t }}</span>
                  <textarea
                    [value]="clientRatingNoteDraft"
                    [disabled]="isClientRatingAdjusting"
                    (input)="onClientRatingNoteChange($event)"
                    [placeholder]="clientRatingReasonRequired ? ('clientReliability.manualReasonRequired' | t) : ''"
                  ></textarea>
                </label>

                <div class="user-profile-page__client-actions-row">
                  <button
                    type="button"
                    class="user-profile-page__action user-profile-page__action--primary"
                    [disabled]="!canSubmitClientRatingAdjustment"
                    (click)="submitClientRatingAdjustment()"
                  >
                    {{ isClientRatingAdjusting ? ('Saving...' | t) : ('clientReliability.manualSubmit' | t) }}
                  </button>
                </div>

                @if (clientRatingAdjustmentErrorText) {
                  <div class="user-profile-page__error">{{ clientRatingAdjustmentErrorText | t }}</div>
                }

                @if (clientRatingAdjustmentSuccessText) {
                  <div class="user-profile-page__success">{{ clientRatingAdjustmentSuccessText }}</div>
                }
              </div>
            </div>
          }

          @if (!isQuickView) {
            <div class="user-profile-page__row user-profile-page__row--wide">
              <span>Client Account Actions</span>
              <div class="user-profile-page__client-actions">
                <label class="user-profile-page__client-email">
                  <span>Email</span>
                  <input
                    type="email"
                    [value]="clientEmailDraft"
                    [disabled]="isClientAccountLoading || isClientEmailSaving"
                    (input)="onClientEmailDraftChange($event)"
                    placeholder="client@example.com"
                  />
                </label>

                <div class="user-profile-page__client-actions-row">
                  <button
                    type="button"
                    class="user-profile-page__action"
                    [disabled]="isClientAccountLoading || isClientEmailSaving || !isClientEmailDraftValid"
                    (click)="saveClientEmail()"
                  >
                    {{ isClientEmailSaving ? 'Saving...' : 'Add/Update email' }}
                  </button>

                  @if (showClientInviteAction) {
                    <button
                      type="button"
                      class="user-profile-page__action user-profile-page__action--primary"
                      [disabled]="!canSendClientInvite || isClientInviteSending"
                      (click)="sendClientInvite()"
                    >
                      {{ isClientInviteSending ? 'Sending...' : clientInviteActionLabel }}
                    </button>
                  }
                </div>

                @if (canManageClientTelegramLink) {
                  <div class="user-profile-page__client-channel">
                    <div class="user-profile-page__client-channel-head">
                      <div>
                        <strong>Telegram</strong>
                        <div class="user-profile-page__account-meta">
                          @if (isClientTelegramLoading) {
                            Loading Telegram link...
                          } @else if (clientTelegramLink?.status === 'active') {
                            Connected{{ clientTelegramLink?.telegramUsername ? ': @' + clientTelegramLink?.telegramUsername : '' }}
                          } @else {
                            Not connected
                          }
                        </div>
                      </div>
                    </div>

                    @if (clientTelegramLinkToken) {
                      <div class="user-profile-page__telegram-link-box">
                        <div>
                          <strong>Connection link</strong>
                          <div class="user-profile-page__account-meta">
                            Expires at {{ clientTelegramLinkToken.expiresAt | date: 'dd.MM.yyyy HH:mm' }}
                          </div>
                        </div>
                        @if (clientTelegramQrDataUrl) {
                          <img
                            [src]="clientTelegramQrDataUrl"
                            alt="Telegram connection QR code"
                            class="user-profile-page__telegram-qr"
                          />
                        }
                        <a [href]="clientTelegramLinkToken.linkUrl" target="_blank" rel="noopener noreferrer">
                          {{ clientTelegramLinkToken.linkUrl }}
                        </a>
                      </div>
                    }

                    <div class="user-profile-page__client-actions-row">
                      <button
                        type="button"
                        class="user-profile-page__action"
                        [disabled]="isClientTelegramLoading || isClientTelegramTokenCreating"
                        (click)="createClientTelegramLinkToken()"
                      >
                        {{ isClientTelegramTokenCreating ? 'Preparing...' : 'Generate Telegram link' }}
                      </button>

                      @if (clientTelegramLinkToken) {
                        <button
                          type="button"
                          class="user-profile-page__action"
                          [disabled]="isClientTelegramLinkCopying"
                          (click)="copyClientTelegramLink()"
                        >
                          {{ isClientTelegramLinkCopying ? 'Copying...' : 'Copy Telegram link' }}
                        </button>
                      }

                      @if (clientTelegramLink?.status === 'active') {
                        <button
                          type="button"
                          class="user-profile-page__action"
                          [disabled]="isClientTelegramRevoking"
                          (click)="revokeClientTelegramLink()"
                        >
                          {{ isClientTelegramRevoking ? 'Revoking...' : 'Revoke Telegram' }}
                        </button>
                      }
                    </div>
                  </div>
                }

                @if (clientAccountActionErrorText) {
                  <div class="user-profile-page__error">{{ clientAccountActionErrorText | t }}</div>
                }

                @if (clientAccountSuccessText) {
                  <div class="user-profile-page__success">{{ clientAccountSuccessText }}</div>
                }
              </div>
            </div>
          }
        }
      </section>

      @if (canEdit && !isQuickView) {
        <button
          type="button"
          class="user-profile-page__edit"
          (click)="openEditModal()"
        >
          {{ 'Edit User' | t }}
        </button>
      }
    }

    <lib-confirm-edit-modal
      [isOpen]="isEditModalOpen && !!item"
      title="Edit User"
      metaPrimaryLabel="User ID"
      [metaPrimaryValue]="item?.id ?? ''"
      metaSecondaryLabel="Role"
      [metaSecondaryValue]="item ? (('role.' + item.role) | t) : ''"
      confirmLabel="Confirm current email to unlock editing"
      confirmPlaceholder="Type current email"
      [confirmValue]="editConfirmEmail"
      (confirmValueChange)="onEditConfirmEmailChange($event)"
      (cancel)="closeEditModal()"
    >
      @if (item) {
        <form [formGroup]="editForm" (ngSubmit)="saveEditModal()" class="user-profile-page__modal-grid">
          <label>
            Email
            <input type="email" formControlName="email" [disabled]="!isEditConfirmValid" />
          </label>

          <label>
            Password (optional)
            <input type="password" formControlName="password" [disabled]="!isEditConfirmValid" />
          </label>

          <label>
            Phone
            <input type="tel" formControlName="phone" [disabled]="!isEditConfirmValid" />
          </label>

          <label>
            Full Name
            <input type="text" formControlName="fullName" [disabled]="!isEditConfirmValid" />
          </label>

          <label>
            Birth Date
            <lib-date-field
              inputId="user-profile-birthDate"
              formControlName="birthDate"
              [readonly]="!isEditConfirmValid"
            />
          </label>

          @if (item.role === 'client') {
            <label class="user-profile-page__modal-field-wide">
              Notes
              <textarea formControlName="notes" [disabled]="!isEditConfirmValid"></textarea>
            </label>
          }

          @if (item.role === 'specialist') {
            <label>
              {{ clinicLabel }}
              <select formControlName="clinicId" [disabled]="!isEditConfirmValid">
                <option value="">Select {{ clinicLabel.toLowerCase() }}</option>
                @for (clinic of clinicOptions; track clinic.clinicId) {
                  <option [value]="clinic.clinicId">{{ clinic.clinicName }}</option>
                }
              </select>
            </label>

            <div class="user-profile-page__modal-field-wide">
              <label>Provided Service Types</label>
              @if (procedureTypeOptions.length > 0) {
                <div
                  class="user-profile-page__procedure-picker"
                  [class.user-profile-page__procedure-picker--disabled]="!isEditConfirmValid"
                >
                  @for (procedureType of procedureTypeOptions; track procedureType.id) {
                    <label class="user-profile-page__procedure-option">
                      <input
                        type="checkbox"
                        [checked]="isProcedureTypeSelected(procedureType.id)"
                        [disabled]="!isEditConfirmValid"
                        (change)="onProcedureTypeToggle(procedureType.id, $any($event.target).checked)"
                      />
                      <span>{{ procedureType.name }}</span>
                    </label>
                  }
                </div>
                <div class="user-profile-page__procedure-meta">
                  Selected: {{ selectedProcedureTypeNamesText || 'None' }}
                </div>
              } @else {
                <div class="user-profile-page__procedure-empty">No active service types found in company catalog.</div>
              }
            </div>
          }

          <div class="user-profile-page__modal-actions">
            <button type="button" class="user-profile-page__action" (click)="closeEditModal()">
              Cancel
            </button>
            <button
              type="submit"
              class="user-profile-page__action user-profile-page__action--primary"
              [disabled]="!isEditConfirmValid || editForm.invalid || isUpdatingUser"
            >
              {{ isUpdatingUser ? 'Saving...' : 'Save changes' }}
            </button>
          </div>
        </form>
      }
    </lib-confirm-edit-modal>
  `,
  styles: [`
    .user-profile-page__head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 16px;
    }

    .user-profile-page__back {
      margin-top: 4px;
      height: 36px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      background: #ffffff;
      padding: 0 12px;
      cursor: pointer;
    }

    .user-profile-page__error {
      margin-bottom: 12px;
      padding: 10px 12px;
      border: 1px solid #fecaca;
      border-radius: 8px;
      background: #fef2f2;
      color: #b91c1c;
      font-size: 14px;
    }

    .user-profile-page__success {
      margin-bottom: 12px;
      padding: 10px 12px;
      border: 1px solid #bbf7d0;
      border-radius: 8px;
      background: #f0fdf4;
      color: #166534;
      font-size: 14px;
    }

    .user-profile-page__procedure-picker {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 10px;
      margin-top: 8px;
      padding: 14px;
      border: 1px solid #dbe2ea;
      border-radius: 14px;
      background: #f8fafc;
    }

    .user-profile-page__procedure-picker--disabled {
      opacity: 0.7;
    }

    .user-profile-page__procedure-option {
      display: flex;
      align-items: center;
      gap: 10px;
      min-height: 44px;
      padding: 10px 12px;
      border: 1px solid #d7dee8;
      border-radius: 12px;
      background: #ffffff;
      cursor: pointer;
    }

    .user-profile-page__procedure-option input {
      width: 16px;
      height: 16px;
      margin: 0;
      flex: 0 0 auto;
    }

    .user-profile-page__procedure-meta {
      margin-top: 8px;
      color: #6b7280;
      font-size: 13px;
    }

    .user-profile-page__procedure-empty {
      margin-top: 8px;
      padding: 12px 14px;
      border: 1px dashed #d1d5db;
      border-radius: 12px;
      background: #f8fafc;
      color: #6b7280;
      font-size: 13px;
    }

    .user-profile-page__card {
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      background: #ffffff;
      padding: 16px;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
      margin-bottom: 12px;
    }

    .user-profile-page__row {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .user-profile-page__row span {
      color: #6b7280;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .user-profile-page__row strong {
      color: #111827;
      font-size: 14px;
      font-weight: 600;
    }

    .user-profile-page__client-score-pill {
      display: inline-flex;
      align-items: center;
      min-height: 32px;
      padding: 0 10px;
      border-radius: 999px;
      border: 1px solid #dbe2ea;
      background: #f8fafc;
      color: #334155;
      font-size: 12px;
      font-weight: 700;
      white-space: nowrap;
      width: fit-content;
    }

    .user-profile-page__client-score-pill--good {
      border-color: #bbf7d0;
      background: #f0fdf4;
      color: #166534;
    }

    .user-profile-page__client-score-pill--warn {
      border-color: #fde68a;
      background: #fffbeb;
      color: #92400e;
    }

    .user-profile-page__client-score-pill--risk {
      border-color: #fecaca;
      background: #fef2f2;
      color: #b91c1c;
    }

    .user-profile-page__photo-block {
      display: grid;
      gap: 12px;
      justify-items: start;
    }

    .user-profile-page__photo-image,
    .user-profile-page__photo-placeholder {
      width: 120px;
      height: 120px;
      border-radius: 16px;
      border: 1px solid #dbe2ea;
      background: #f8fafc;
    }

    .user-profile-page__photo-image {
      object-fit: cover;
    }

    .user-profile-page__photo-placeholder {
      display: grid;
      place-items: center;
      padding: 12px;
      color: #6b7280;
      font-size: 12px;
      text-align: center;
    }

    .user-profile-page__photo-controls {
      display: grid;
      gap: 8px;
      justify-items: start;
    }

    .user-profile-page__account-status-block {
      display: grid;
      gap: 4px;
      justify-items: start;
    }

    .user-profile-page__account-meta {
      color: #6b7280;
      font-size: 12px;
    }

    .user-profile-page__rating-history {
      display: grid;
      gap: 8px;
      width: 100%;
    }

    .user-profile-page__rating-history-item {
      display: grid;
      gap: 4px;
      padding: 10px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      background: #f8fafc;
    }

    .user-profile-page__rating-history-top {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: center;
    }

    .user-profile-page__rating-delta {
      display: inline-flex;
      align-items: center;
      min-height: 24px;
      padding: 0 8px;
      border: 1px solid #dbe2ea;
      border-radius: 999px;
      background: #f8fafc;
      color: #475569;
      font-size: 12px;
      font-weight: 700;
      white-space: nowrap;
    }

    .user-profile-page__rating-delta--positive {
      border-color: #bbf7d0;
      background: #f0fdf4;
      color: #166534;
    }

    .user-profile-page__rating-delta--negative {
      border-color: #fecaca;
      background: #fef2f2;
      color: #b91c1c;
    }

    .user-profile-page__row--wide {
      grid-column: 1 / -1;
    }

    .user-profile-page__edit {
      height: 38px;
      border: none;
      border-radius: 8px;
      background: #2563eb;
      color: #ffffff;
      padding: 0 14px;
      cursor: pointer;
    }

    .user-profile-page__modal-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 12px;
      align-items: end;
    }

    .user-profile-page__modal-grid label {
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 13px;
      color: #374151;
      font-weight: 600;
    }

    .user-profile-page__modal-grid input[type='email'],
    .user-profile-page__modal-grid input[type='password'],
    .user-profile-page__modal-grid input[type='tel'],
    .user-profile-page__modal-grid input[type='text'],
    .user-profile-page__modal-grid select {
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 10px;
      box-sizing: border-box;
    }

    .user-profile-page__modal-grid textarea {
      min-height: 96px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 8px 10px;
      box-sizing: border-box;
      resize: vertical;
      font: inherit;
    }

    .user-profile-page__modal-grid select[multiple] {
      min-height: 120px;
      padding: 6px 10px;
    }

    .user-profile-page__modal-field-wide {
      grid-column: 1 / -1;
    }

    .user-profile-page__modal-actions {
      grid-column: 1 / -1;
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 6px;
    }

    .user-profile-page__action {
      height: 32px;
      border-radius: 8px;
      border: 1px solid #2563eb;
      background: #ffffff;
      color: #2563eb;
      padding: 0 10px;
      font-size: 13px;
      cursor: pointer;
    }

    .user-profile-page__action--primary {
      background: #2563eb;
      color: #ffffff;
    }

    .user-profile-page__client-actions {
      display: grid;
      gap: 8px;
      width: 100%;
    }

    .user-profile-page__client-email {
      display: grid;
      gap: 6px;
      max-width: 420px;
      font-size: 13px;
      color: #374151;
      font-weight: 600;
    }

    .user-profile-page__client-email input {
      height: 36px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 10px;
      box-sizing: border-box;
      font: inherit;
    }

    .user-profile-page__client-email textarea,
    .user-profile-page__client-rating-adjustment input {
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 8px 10px;
      box-sizing: border-box;
      font: inherit;
    }

    .user-profile-page__client-email textarea {
      min-height: 88px;
      resize: vertical;
    }

    .user-profile-page__client-email--wide {
      max-width: 640px;
    }

    .user-profile-page__client-rating-adjustment {
      display: grid;
      gap: 6px;
      max-width: 180px;
      font-size: 13px;
      color: #374151;
      font-weight: 600;
    }

    .user-profile-page__client-actions-row {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: center;
    }

    .user-profile-page__client-channel {
      display: grid;
      gap: 10px;
      padding-top: 12px;
      border-top: 1px solid #e5e7eb;
    }

    .user-profile-page__client-channel-head {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: flex-start;
    }

    .user-profile-page__communication {
      display: grid;
      gap: 10px;
      width: 100%;
      max-width: 760px;
      padding: 12px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: #f8fafc;
    }

    .user-profile-page__communication-head {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: flex-start;
    }

    .user-profile-page__communication-status {
      flex: 0 0 auto;
      padding: 4px 8px;
      border-radius: 999px;
      border: 1px solid #d1d5db;
      background: #fff;
      font-size: 12px;
      font-weight: 700;
      color: #374151;
    }

    .user-profile-page__communication-status--automatic {
      border-color: #bbf7d0;
      background: #f0fdf4;
      color: #166534;
    }

    .user-profile-page__communication-status--manual {
      border-color: #fde68a;
      background: #fffbeb;
      color: #92400e;
    }

    .user-profile-page__communication-status--unreachable {
      border-color: #fecaca;
      background: #fef2f2;
      color: #991b1b;
    }

    .user-profile-page__communication-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 8px;
    }

    .user-profile-page__communication-channel {
      display: grid;
      gap: 4px;
      min-width: 0;
      padding: 10px;
      border: 1px solid #e5e7eb;
      border-left-width: 3px;
      border-radius: 8px;
      background: #fff;
    }

    .user-profile-page__communication-channel--automatic {
      border-left-color: #22c55e;
    }

    .user-profile-page__communication-channel--manual {
      border-left-color: #f59e0b;
    }

    .user-profile-page__communication-channel--unreachable {
      border-left-color: #ef4444;
    }

    .user-profile-page__communication-channel span {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      color: #6b7280;
    }

    .user-profile-page__communication-channel strong,
    .user-profile-page__communication-channel small {
      overflow-wrap: anywhere;
    }

    .user-profile-page__communication-channel small {
      color: #6b7280;
      line-height: 1.3;
    }

    .user-profile-page__telegram-link-box {
      display: grid;
      gap: 8px;
      padding: 12px;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
      background: #eff6ff;
    }

    .user-profile-page__telegram-link-box a {
      overflow-wrap: anywhere;
    }

    .user-profile-page__telegram-qr {
      width: 180px;
      height: 180px;
      border: 1px solid #dbeafe;
      border-radius: 8px;
      background: #fff;
      padding: 8px;
    }

    @media (max-width: 760px) {
      .user-profile-page__communication-head {
        display: grid;
      }

      .user-profile-page__communication-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class UserProfilePageComponent implements OnInit
{
  private static readonly DEFAULT_CLINIC_LABEL = 'Clinic';
  private static readonly EMAIL_REGEXP = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  @Input() public userIdOverride: string | null = null;
  @Input() public clientIdOverride: string | null = null;
  @Input() public companyIdOverride: string | null = null;
  @Input() public presentationMode: EntityPresentationMode = 'full-view';
  @Output() public readonly closeRequested = new EventEmitter<void>();

  private readonly usersService = inject(UsersService);
  private readonly clientsAdminService = inject(ClientsAdminService);
  private readonly procedureTypesService = inject(ProcedureTypesService);
  private readonly settingsService = inject(SettingsService);
  private readonly telegramConnectorService = inject(TelegramConnectorService);
  private readonly authService = inject(AuthService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  protected item: UserItem | null = null;
  protected isLoading = true;
  protected isUpdatingUser = false;
  protected errorText: string | null = null;
  protected actionErrorText: string | null = null;
  protected successText: string | null = null;
  protected isEditModalOpen = false;
  protected editConfirmEmail = '';
  protected clinicOptions: ClinicOperationalSettingsDto[] = [];
  protected ratingSettings: CompanyRatingSettingsDto | null = null;
  protected procedureTypeOptions: ProcedureTypeItem[] = [];
  protected currentUserDetail: UserDetailItem | null = null;
  protected clinicLabel = UserProfilePageComponent.DEFAULT_CLINIC_LABEL;
  protected canEdit = false;
  protected selectedPhotoFile: File | null = null;
  protected selectedPhotoName = '';
  protected isPhotoUploading = false;
  protected isPhotoRemoving = false;
  protected clientAccount: ClientAdminItem | null = null;
  protected clientRatingHistory: UserClientRatingEvent[] = [];
  protected isClientAccountLoading = false;
  protected isClientRatingHistoryLoading = false;
  protected isClientRatingAdjusting = false;
  protected isClientEmailSaving = false;
  protected isClientInviteSending = false;
  protected isClientTelegramLoading = false;
  protected isClientTelegramTokenCreating = false;
  protected isClientTelegramLinkCopying = false;
  protected isClientTelegramRevoking = false;
  protected clientEmailDraft = '';
  protected clientTelegramLink: TelegramLinkDto | null = null;
  protected clientTelegramLinkToken: TelegramLinkTokenDto | null = null;
  protected clientTelegramQrDataUrl: string | null = null;
  protected clientAccountActionErrorText: string | null = null;
  protected clientAccountSuccessText: string | null = null;
  protected clientRatingAdjustmentErrorText: string | null = null;
  protected clientRatingAdjustmentSuccessText: string | null = null;
  protected clientRatingDeltaDraft = 0;
  protected clientRatingNoteDraft = '';

  protected readonly editForm = this.formBuilder.group({
    email: this.formBuilder.nonNullable.control('', [
      Validators.required,
      Validators.email
    ]),
    password: this.formBuilder.nonNullable.control('', [
      Validators.minLength(8)
    ]),
    phone: this.formBuilder.nonNullable.control(''),
    fullName: this.formBuilder.nonNullable.control('', [Validators.required, Validators.maxLength(255)]),
    birthDate: this.formBuilder.nonNullable.control(''),
    notes: this.formBuilder.nonNullable.control('', [Validators.maxLength(5000)]),
    clinicId: this.formBuilder.nonNullable.control(''),
    procedureTypeIds: this.formBuilder.nonNullable.control<string[]>([])
  });

  protected get specialistProcedureTypeLabels(): string[] {
    if (!this.item || this.item.role !== 'specialist') {
      return [];
    }

    const detailProcedureTypes = this.currentUserDetail?.specialistProcedureTypes ?? [];
    if (detailProcedureTypes.length > 0) {
      return detailProcedureTypes.map((item) => item.name);
    }

    const selected = new Set(this.item.specialistProcedureTypeIds ?? []);

    return this.procedureTypeOptions
      .filter((item) => selected.has(item.id))
      .map((item) => item.name);
  }

  protected get isQuickView(): boolean
  {
    return this.presentationMode === 'quick-view';
  }

  protected get selectedProcedureTypeNamesText(): string {
    const selectedIds = new Set(this.editForm.controls.procedureTypeIds.value);
    const names = this.procedureTypeOptions
      .filter((item) => selectedIds.has(item.id))
      .map((item) => item.name);

    return names.join(', ');
  }

  protected get isSystemAdmin(): boolean
  {
    return (this.authService.getCurrentRole() as UserRole | null) === 'system_admin';
  }

  protected get isEditConfirmValid(): boolean
  {
    const currentEmail = this.item?.email.trim().toLowerCase() ?? '';
    return this.editConfirmEmail.trim().toLowerCase() === currentEmail;
  }

  protected get clientAccountStatusLabel(): string {
    if (this.isClientAccountLoading) {
      return 'Loading...';
    }

    if (!this.clientAccount) {
      return 'No account';
    }

    if (this.clientAccount.accountStatus === 'registered') {
      return 'Registered';
    }

    if (this.clientAccount.accountStatus === 'invited') {
      return 'Invited';
    }

    return 'No account';
  }

  protected get clientReliabilityPercent(): number {
    if (this.clientAccount) {
      return this.clientAccount.ratingPercent;
    }

    return this.item?.clientProfile?.ratingPercent ?? 100;
  }

  protected get clientReliabilityLabel(): string {
    if (this.clientReliabilityPercent >= 85) {
      return 'clientReliability.label.reliable';
    }

    if (this.clientReliabilityPercent >= 60) {
      return 'clientReliability.label.watch';
    }

    return 'clientReliability.label.risk';
  }

  protected get clientReliabilityTone(): 'good' | 'warn' | 'risk' {
    if (this.clientReliabilityPercent >= 85) {
      return 'good';
    }

    if (this.clientReliabilityPercent >= 60) {
      return 'warn';
    }

    return 'risk';
  }

  protected get clientCommunicationSummary(): ClientCommunicationSummaryView | null {
    if (!this.clientAccount) {
      return null;
    }

    return presentClientCommunicationChannels(this.clientAccount.communicationChannels);
  }

  protected get clientRatingReasonRequired(): boolean {
    return !!this.ratingSettings?.manualAdjustmentAllowed
      && !!this.ratingSettings?.manualAdjustmentReasonRequired;
  }

  protected get canManualAdjustClientReliability(): boolean {
    return !!this.item
      && this.item.role === 'client'
      && !!this.canEdit
      && !!this.ratingSettings?.manualAdjustmentAllowed;
  }

  protected get canSubmitClientRatingAdjustment(): boolean {
    if (!this.canManualAdjustClientReliability || this.isClientRatingAdjusting) {
      return false;
    }

    if (!Number.isFinite(this.clientRatingDeltaDraft) || this.clientRatingDeltaDraft === 0) {
      return false;
    }

    if (this.clientRatingReasonRequired && !this.clientRatingNoteDraft.trim()) {
      return false;
    }

    return true;
  }

  protected get isClientEmailDraftValid(): boolean {
    const value = this.clientEmailDraft.trim().toLowerCase();
    return UserProfilePageComponent.EMAIL_REGEXP.test(value);
  }

  protected get canSendClientInvite(): boolean {
    if (!this.clientAccount || this.isClientAccountLoading) {
      return false;
    }

    if (this.clientAccount.accountStatus === 'registered') {
      return false;
    }

    const email = this.clientAccount.email?.trim().toLowerCase() ?? '';
    return UserProfilePageComponent.EMAIL_REGEXP.test(email);
  }

  protected get clientInviteActionLabel(): string {
    if (this.clientAccount?.accountStatus === 'invited') {
      return 'Resend invite';
    }

    return 'Send invite';
  }

  protected get showClientInviteAction(): boolean {
    return this.clientAccount?.accountStatus !== 'registered';
  }

  protected get canManageClientTelegramLink(): boolean {
    if (this.isQuickView || !this.clientAccount || this.item?.role !== 'client') {
      return false;
    }

    const role = this.authService.getCurrentRole() as UserRole | null;
    return role === 'system_admin' ||
      role === 'company_admin' ||
      role === 'manager' ||
      role === 'reception';
  }

  public ngOnInit(): void
  {
    this.canEdit = this.isAllowedRole(this.authService.getCurrentRole() as UserRole | null);
    this.loadReferenceData();
    this.loadUser();
  }

  protected goBack(): void
  {
    if (this.isQuickView) {
      this.closeRequested.emit();
      return;
    }

    const routeSlug = this.route.snapshot.paramMap.get('companySlug')?.trim() ?? '';
    void this.router.navigate(this.withTenantPath(['users'], routeSlug || undefined), {
      queryParams: this.systemAdminCompanyId ? { companyId: this.systemAdminCompanyId } : undefined,
    });
  }

  protected openEditModal(): void
  {
    if (!this.item || !this.canEdit) {
      return;
    }

    this.actionErrorText = null;
    this.successText = null;
    this.editConfirmEmail = '';
    this.isUpdatingUser = false;

    this.editForm.patchValue({
      email: this.item.email,
      password: '',
      phone: this.item.phone ?? '',
      fullName: this.item.fullName,
      birthDate: this.normalizeBirthDateForInput(this.item.birthDate ?? null),
      notes: this.item.clientProfile?.notes ?? '',
      clinicId: this.item.role === 'specialist'
        ? (this.item.clinicId ?? '')
        : '',
      procedureTypeIds: this.item.role === 'specialist'
        ? (this.item.specialistProcedureTypeIds ?? [])
        : []
    });

    this.editForm.markAsPristine();
    this.editForm.markAsUntouched();
    this.isEditModalOpen = true;
  }

  protected closeEditModal(): void
  {
    this.isEditModalOpen = false;
    this.editConfirmEmail = '';
    this.isUpdatingUser = false;
  }

  protected onEditConfirmEmailChange(value: string): void
  {
    this.editConfirmEmail = value;
  }

  protected isProcedureTypeSelected(procedureTypeId: string): boolean {
    return this.editForm.controls.procedureTypeIds.value.includes(procedureTypeId);
  }

  protected onProcedureTypeToggle(procedureTypeId: string, checked: boolean): void {
    const current = this.normalizeIdList(this.editForm.controls.procedureTypeIds.value);

    if (checked) {
      if (!current.includes(procedureTypeId)) {
        this.editForm.controls.procedureTypeIds.setValue([...current, procedureTypeId]);
      }
      return;
    }

    this.editForm.controls.procedureTypeIds.setValue(
      current.filter((id) => id !== procedureTypeId),
    );
  }

  protected saveEditModal(): void
  {
    if (!this.item || !this.isEditConfirmValid || this.editForm.invalid || this.isUpdatingUser)
    {
      return;
    }

    const value = this.editForm.getRawValue();
    const payload: UpdateUserRequest = {
      confirmEmail: this.editConfirmEmail.trim().toLowerCase(),
      email: value.email.trim().toLowerCase(),
      password: value.password.trim() ? value.password.trim() : undefined,
      phone: value.phone.trim(),
      fullName: value.fullName.trim() ? value.fullName.trim() : undefined,
      birthDate: value.birthDate.trim() ? value.birthDate.trim() : undefined,
      notes: this.item.role === 'client'
        ? value.notes.trim()
        : undefined,
      clinicId: this.item.role === 'specialist'
        ? (value.clinicId.trim() ? value.clinicId.trim() : undefined)
        : undefined,
      procedureTypeIds: this.item.role === 'specialist'
        ? this.normalizeIdList(value.procedureTypeIds)
        : undefined
    };

    if (!payload.email || !payload.confirmEmail)
    {
      return;
    }

    if (!value.fullName.trim()) {
      this.actionErrorText = 'Full name is required';
      return;
    }

    if (
      this.item.role === 'specialist' &&
      (!payload.procedureTypeIds || payload.procedureTypeIds.length === 0)
    ) {
      this.actionErrorText = 'Select at least one service type for specialist';
      return;
    }

    if (this.item.role === 'specialist' && !payload.clinicId) {
      this.actionErrorText = `Select ${this.clinicLabel.toLowerCase()} for specialist`;
      return;
    }

    this.isUpdatingUser = true;
    this.actionErrorText = null;
    this.successText = null;

    if (this.item.sourceKind === 'client') {
      const clientId = this.item.clientProfile?.id ?? '';
      if (!clientId) {
        this.isUpdatingUser = false;
        this.actionErrorText = 'Failed to resolve client record';
        return;
      }

      this.clientsAdminService.update(
        clientId,
        {
          fullName: value.fullName.trim() ? value.fullName.trim() : undefined,
          phone: value.phone.trim() ? value.phone.trim() : undefined,
          birthDate: value.birthDate.trim() ? value.birthDate.trim() : undefined,
          notes: value.notes.trim() ? value.notes.trim() : undefined,
        },
        this.systemAdminCompanyId ?? undefined,
      ).subscribe({
        next: (updatedClient) => {
          this.item = this.mapClientToUserItem(updatedClient);
          this.clientAccount = updatedClient;
          this.clientEmailDraft = updatedClient.email ?? '';
          this.isUpdatingUser = false;
          this.successText = `Client "${updatedClient.fullName}" updated.`;
          this.closeEditModal();
        },
        error: (error: unknown) => {
          this.isUpdatingUser = false;
          this.actionErrorText = resolveHttpErrorMessageKey(error, {
            forbidden: 'error.forbiddenUserManagement',
            fallback: 'error.generic'
          });
        },
      });
      return;
    }

    this.usersService.update(
      this.item.id,
      payload,
      this.systemAdminCompanyId ?? undefined,
    ).subscribe({
      next: (updated) =>
      {
        this.item = updated;
        if (updated.role === 'client') {
          this.loadClientAccount();
        }
        this.isUpdatingUser = false;
        this.successText = `User "${updated.email}" updated.`;
        this.closeEditModal();
      },
      error: (error: unknown) =>
      {
        this.isUpdatingUser = false;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenUserManagement',
          fallback: 'error.generic'
        });
      }
    });
  }

  protected onClientEmailDraftChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.clientEmailDraft = target?.value ?? '';
  }

  protected onPhotoSelected(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const file = target?.files?.[0] ?? null;
    this.selectedPhotoFile = file;
    this.selectedPhotoName = file?.name ?? '';
    if (target) {
      target.value = '';
    }
  }

  protected uploadPhoto(): void {
    const targetUserId = this.item?.linkedUserId?.trim() ?? '';
    if (!this.item || !targetUserId || !this.canEdit || !this.selectedPhotoFile || this.isPhotoUploading || this.isPhotoRemoving) {
      return;
    }

    this.isPhotoUploading = true;
    this.actionErrorText = null;
    this.successText = null;

    this.usersService.uploadPhoto(
      targetUserId,
      this.selectedPhotoFile,
      this.systemAdminCompanyId ?? undefined,
    ).subscribe({
      next: (updated) => {
        this.item = updated;
        this.selectedPhotoFile = null;
        this.selectedPhotoName = '';
        this.isPhotoUploading = false;
        this.successText = 'Profile photo uploaded.';
      },
      error: (error: unknown) => {
        this.isPhotoUploading = false;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'error.generic',
        });
      },
    });
  }

  protected removePhoto(): void {
    const targetUserId = this.item?.linkedUserId?.trim() ?? '';
    if (!this.item || !targetUserId || !this.canEdit || !this.item.photoUrl || this.isPhotoUploading || this.isPhotoRemoving) {
      return;
    }

    this.isPhotoRemoving = true;
    this.actionErrorText = null;
    this.successText = null;

    this.usersService.removePhoto(
      targetUserId,
      this.systemAdminCompanyId ?? undefined,
    ).subscribe({
      next: (updated) => {
        this.item = updated;
        this.selectedPhotoFile = null;
        this.selectedPhotoName = '';
        this.isPhotoRemoving = false;
        this.successText = 'Profile photo removed.';
      },
      error: (error: unknown) => {
        this.isPhotoRemoving = false;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'error.generic',
        });
      },
    });
  }

  protected saveClientEmail(): void {
    if (!this.clientAccount || !this.isClientEmailDraftValid || this.isClientEmailSaving) {
      return;
    }

    const email = this.clientEmailDraft.trim().toLowerCase();
    this.isClientEmailSaving = true;
    this.clientAccountActionErrorText = null;
    this.clientAccountSuccessText = null;

    this.clientsAdminService.updateContact(
      this.clientAccount.id,
      { email },
      this.systemAdminCompanyId ?? undefined,
    ).subscribe({
      next: (client) => {
        this.clientAccount = client;
        this.clientEmailDraft = client.email ?? '';
        this.clientAccountSuccessText = `Email updated to "${client.email ?? ''}".`;
        this.isClientEmailSaving = false;
      },
      error: (error: unknown) => {
        this.isClientEmailSaving = false;
        this.clientAccountActionErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'Email is already used by another client',
        });
      },
    });
  }

  protected sendClientInvite(): void {
    if (!this.clientAccount || !this.canSendClientInvite || this.isClientInviteSending) {
      return;
    }

    this.isClientInviteSending = true;
    this.clientAccountActionErrorText = null;
    this.clientAccountSuccessText = null;

    this.clientsAdminService.sendInvite(
      this.clientAccount.id,
      {},
      this.systemAdminCompanyId ?? undefined,
    ).subscribe({
      next: (response) => {
        this.clientAccountSuccessText =
          `Invite sent. Expires at ${new Date(response.expiresAt).toLocaleString()}.`;
        this.isClientInviteSending = false;
        this.loadClientAccount();
      },
      error: (error: unknown) => {
        this.isClientInviteSending = false;
        this.clientAccountActionErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'Invite conflict: email occupied or client already linked',
        });
      },
    });
  }

  protected createClientTelegramLinkToken(): void {
    if (!this.clientAccount || !this.canManageClientTelegramLink || this.isClientTelegramTokenCreating) {
      return;
    }

    this.isClientTelegramTokenCreating = true;
    this.clientAccountActionErrorText = null;
    this.clientAccountSuccessText = null;

    this.telegramConnectorService.createClientLinkToken(
      this.clientAccount.id,
      this.systemAdminCompanyId ?? undefined,
    ).subscribe({
      next: (token) => {
        this.clientTelegramLinkToken = token;
        void this.updateClientTelegramQr(token.linkUrl);
        this.clientAccountSuccessText = 'Telegram connection link generated.';
        this.isClientTelegramTokenCreating = false;
      },
      error: (error: unknown) => {
        this.isClientTelegramTokenCreating = false;
        this.clientAccountActionErrorText = this.resolveTelegramError(error);
      },
    });
  }

  protected copyClientTelegramLink(): void {
    const linkUrl = this.clientTelegramLinkToken?.linkUrl;
    if (!linkUrl || this.isClientTelegramLinkCopying) {
      return;
    }

    this.isClientTelegramLinkCopying = true;
    this.clientAccountActionErrorText = null;
    this.clientAccountSuccessText = null;

    if (!navigator.clipboard) {
      this.clientAccountActionErrorText = 'error.generic';
      this.isClientTelegramLinkCopying = false;
      return;
    }

    void navigator.clipboard.writeText(linkUrl)
      .then(() => {
        this.clientAccountSuccessText = 'Telegram connection link copied.';
      })
      .catch(() => {
        this.clientAccountActionErrorText = 'error.generic';
      })
      .finally(() => {
        this.isClientTelegramLinkCopying = false;
      });
  }

  protected revokeClientTelegramLink(): void {
    if (!this.clientAccount || !this.canManageClientTelegramLink || this.isClientTelegramRevoking) {
      return;
    }

    this.isClientTelegramRevoking = true;
    this.clientAccountActionErrorText = null;
    this.clientAccountSuccessText = null;

    this.telegramConnectorService.revokeClientLink(
      this.clientAccount.id,
      this.systemAdminCompanyId ?? undefined,
    ).subscribe({
      next: (link) => {
        this.clientTelegramLink = link;
        this.syncClientTelegramCommunicationChannel(link);
        this.clientTelegramLinkToken = null;
        this.clientTelegramQrDataUrl = null;
        this.clientAccountSuccessText = 'Telegram link revoked.';
        this.isClientTelegramRevoking = false;
      },
      error: (error: unknown) => {
        this.isClientTelegramRevoking = false;
        this.clientAccountActionErrorText = this.resolveTelegramError(error);
      },
    });
  }

  protected clinicDisplayName(clinicId: string): string {
    if (this.currentUserDetail?.clinic?.id === clinicId) {
      return this.currentUserDetail.clinic.name;
    }

    return this.clinicOptions.find((item) => item.clinicId === clinicId)?.clinicName ?? clinicId;
  }

  private get userId(): string
  {
    const override = this.userIdOverride?.trim() ?? '';
    if (override) {
      return override;
    }

    const routeId = this.route.snapshot.paramMap.get('id')?.trim() ?? '';
    return routeId.startsWith('client:') ? '' : routeId;
  }

  private get clientId(): string {
    const override = this.clientIdOverride?.trim() ?? '';
    if (override) {
      return override;
    }

    const routeId = this.route.snapshot.paramMap.get('id')?.trim() ?? '';
    return routeId.startsWith('client:') ? routeId.slice('client:'.length).trim() : '';
  }

  private get systemAdminCompanyId(): string | null
  {
    const override = this.companyIdOverride?.trim() ?? '';
    if (override) {
      return override;
    }

    const companyId = this.route.snapshot.queryParamMap.get('companyId')?.trim() ?? '';
    return companyId || this.systemAdminContext.getCompanyId()?.trim() || null;
  }

  private loadUser(): void
  {
    const clientId = this.clientId;
    if (clientId) {
      this.loadClientUser(clientId);
      return;
    }

    const userId = this.userId;

    if (!userId) {
      this.errorText = 'Failed to load user';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.errorText = null;

    const request$ = this.usersService.getDetailById(userId, this.systemAdminCompanyId ?? undefined);

    request$.subscribe({
      next: (item) =>
      {
        this.item = item;
        this.currentUserDetail = this.isUserDetailItem(item) ? item : null;
        this.clientAccount = null;
        this.clientRatingHistory = [];
        this.clientEmailDraft = '';
        this.resetClientTelegramState();
        this.clientAccountActionErrorText = null;
        this.clientAccountSuccessText = null;
        if (item.role === 'client') {
          this.loadClientAccount();
        } else {
          this.isClientAccountLoading = false;
          this.isClientRatingHistoryLoading = false;
        }
        this.isLoading = false;
      },
      error: (error: unknown) =>
      {
        this.errorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenUserManagement',
          fallback: 'Failed to load user'
        });
        this.isLoading = false;
      }
    });
  }

  private loadClientUser(clientId: string): void {
    this.isLoading = true;
    this.errorText = null;

    this.clientsAdminService.getDetailById(clientId, this.systemAdminCompanyId ?? undefined).subscribe({
      next: (client) => {
        this.item = this.mapClientToUserItem(client);
        this.currentUserDetail = null;
        this.clientAccount = client;
        this.clientRatingHistory = client.ratingEvents;
        this.clientEmailDraft = client.email ?? '';
        this.loadClientTelegramLink(client.id);
        this.clientAccountActionErrorText = null;
        this.clientAccountSuccessText = null;
        this.isClientAccountLoading = false;
        this.isClientRatingHistoryLoading = false;
        this.isLoading = false;
      },
      error: (error: unknown) => {
        this.errorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenUserManagement',
          fallback: 'Failed to load user'
        });
        this.isLoading = false;
      },
    });
  }

  private loadClientAccount(): void {
    const clientId = this.item?.role === 'client'
      ? (this.item.clientProfile?.id ?? '')
      : '';

    if (!clientId) {
      this.clientAccount = null;
      this.clientRatingHistory = [];
      this.clientEmailDraft = '';
      this.resetClientTelegramState();
      this.isClientAccountLoading = false;
      this.isClientRatingHistoryLoading = false;
      return;
    }

    this.isClientAccountLoading = true;
    this.isClientRatingHistoryLoading = true;
    this.clientsAdminService.getDetailById(
      clientId,
      this.systemAdminCompanyId ?? undefined,
    ).subscribe({
      next: (client) => {
        this.clientAccount = client;
        this.clientRatingHistory = client.ratingEvents;
        this.clientEmailDraft = client.email ?? '';
        this.loadClientTelegramLink(client.id);
        this.isClientAccountLoading = false;
        this.isClientRatingHistoryLoading = false;
      },
      error: (error: unknown) => {
        this.clientAccount = null;
        this.clientRatingHistory = [];
        this.clientEmailDraft = '';
        this.resetClientTelegramState();
        this.clientAccountActionErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'Failed to load client account',
        });
        this.isClientAccountLoading = false;
        this.isClientRatingHistoryLoading = false;
      }
    });
  }

  protected onClientRatingDeltaChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const nextValue = Number(target?.value ?? '0');
    this.clientRatingDeltaDraft = Number.isFinite(nextValue) ? nextValue : 0;
    this.clientRatingAdjustmentErrorText = null;
    this.clientRatingAdjustmentSuccessText = null;
  }

  protected onClientRatingNoteChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement | null;
    this.clientRatingNoteDraft = target?.value ?? '';
    this.clientRatingAdjustmentErrorText = null;
    this.clientRatingAdjustmentSuccessText = null;
  }

  protected submitClientRatingAdjustment(): void {
    if (!this.item || !this.canSubmitClientRatingAdjustment) {
      return;
    }

    const payload: ManualAdjustUserClientRatingRequest = {
      deltaPercent: this.clientRatingDeltaDraft,
      note: this.clientRatingNoteDraft.trim() || undefined,
    };

    this.isClientRatingAdjusting = true;
    this.clientRatingAdjustmentErrorText = null;
    this.clientRatingAdjustmentSuccessText = null;

    this.usersService.manualAdjustClientRating(
      this.item.id,
      payload,
      this.systemAdminCompanyId ?? undefined,
    ).subscribe({
      next: () => {
        this.clientRatingDeltaDraft = 0;
        this.clientRatingNoteDraft = '';
        this.clientRatingAdjustmentSuccessText = 'clientReliability.manualAdjustmentSuccess';
        this.isClientRatingAdjusting = false;
        this.loadUser();
      },
      error: (error: unknown) => {
        this.clientRatingAdjustmentErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'clientReliability.manualAdjustmentError',
        });
        this.isClientRatingAdjusting = false;
      }
    });
  }

  private mapClientToUserItem(client: ClientAdminItem): UserItem {
    return {
      id: `client:${client.id}`,
      sourceKind: 'client',
      linkedUserId: client.userId ?? null,
      clinicId: null,
      clinicName: null,
      photoUrl: client.photoUrl ?? null,
      fullName: client.fullName,
      birthDate: client.birthDate,
      email: client.email ?? '',
      phone: client.phone ?? null,
      role: 'client',
      isActive: client.accountStatus === 'registered',
      accountStatus: client.accountStatus,
      inviteExpiresAt: client.inviteExpiresAt,
      accountOrigin: client.accountOrigin,
      clientProfile: {
        id: client.id,
        fullName: client.fullName,
        ratingPercent: client.ratingPercent,
        birthDate: client.birthDate,
        notes: client.notes,
      },
      specialistProcedureTypeIds: [],
      createdAt: client.createdAt,
      updatedAt: client.updatedAt,
    };
  }

  private loadClientTelegramLink(clientId: string): void {
    this.resetClientTelegramState();

    if (!this.canManageClientTelegramLink) {
      return;
    }

    this.isClientTelegramLoading = true;
    this.telegramConnectorService.getClientLink(
      clientId,
      this.systemAdminCompanyId ?? undefined,
    ).subscribe({
      next: (link) => {
        this.clientTelegramLink = link;
        this.syncClientTelegramCommunicationChannel(link);
        this.isClientTelegramLoading = false;
      },
      error: () => {
        this.clientTelegramLink = null;
        this.isClientTelegramLoading = false;
      },
    });
  }

  private syncClientTelegramCommunicationChannel(link: TelegramLinkDto | null): void {
    if (!this.clientAccount) {
      return;
    }

    const currentChannels = this.clientAccount.communicationChannels;
    const telegramStatus = link?.status ?? 'unlinked';
    const nextChannels: ClientCommunicationChannels = {
      ...currentChannels,
      telegram: {
        status: telegramStatus,
        deliveryMode: telegramStatus === 'active' ? 'automatic' : 'none',
        username: link?.telegramUsername ?? null,
        linkedAt: link?.linkedAt ?? null,
        revokedAt: link?.revokedAt ?? null,
        blockedAt: link?.blockedAt ?? null,
        lastDeliveryFailedAt: link?.lastDeliveryFailedAt ?? null,
        lastDeliveryError: link?.lastDeliveryError ?? null,
      },
    };
    const automaticChannels = new Set(nextChannels.summary.automaticChannels);

    if (telegramStatus === 'active') {
      automaticChannels.add('telegram');
    } else {
      automaticChannels.delete('telegram');
    }

    const automaticChannelList = Array.from(automaticChannels);
    const manualChannels = nextChannels.summary.manualChannels;

    this.clientAccount = {
      ...this.clientAccount,
      communicationChannels: {
        ...nextChannels,
        summary: {
          automaticReachable: automaticChannelList.length > 0,
          manualReachable: manualChannels.length > 0,
          manualFollowUpRequired: automaticChannelList.length === 0 && manualChannels.length > 0,
          reachabilityStatus: automaticChannelList.length > 0
            ? 'automatic'
            : manualChannels.length > 0
              ? 'manual'
              : 'unreachable',
          automaticChannels: automaticChannelList,
          manualChannels,
        },
      },
    };
  }

  private resetClientTelegramState(): void {
    this.clientTelegramLink = null;
    this.clientTelegramLinkToken = null;
    this.clientTelegramQrDataUrl = null;
    this.isClientTelegramLoading = false;
    this.isClientTelegramTokenCreating = false;
    this.isClientTelegramLinkCopying = false;
    this.isClientTelegramRevoking = false;
  }

  private async updateClientTelegramQr(linkUrl: string): Promise<void> {
    try {
      this.clientTelegramQrDataUrl = await toDataURL(linkUrl, {
        errorCorrectionLevel: 'M',
        margin: 2,
        width: 180,
      });
    } catch {
      this.clientTelegramQrDataUrl = null;
    } finally {
      this.changeDetectorRef.detectChanges();
    }
  }

  private loadReferenceData(): void
  {
    if (this.isQuickView) {
      return;
    }

    this.settingsService.getOperationsSettings(this.systemAdminCompanyId ?? undefined).subscribe({
      next: (settings) => {
        this.clinicLabel = this.normalizeLabel(
          settings.clinicLabel,
          UserProfilePageComponent.DEFAULT_CLINIC_LABEL,
        );
      },
      error: () => {
        this.clinicLabel = UserProfilePageComponent.DEFAULT_CLINIC_LABEL;
      },
    });

    this.settingsService.getRatingSettings(this.systemAdminCompanyId ?? undefined).subscribe({
      next: (settings) => {
        this.ratingSettings = settings;
      },
      error: () => {
        this.ratingSettings = null;
      }
    });

    this.settingsService.getClinicOperationalSettings(this.systemAdminCompanyId ?? undefined).subscribe({
      next: (items: ClinicOperationalSettingsDto[]) => {
        this.clinicOptions = items;
      },
      error: () => {
        this.clinicOptions = [];
      },
    });

    this.procedureTypesService.getList({
      companyId: this.systemAdminCompanyId ?? undefined,
    }).subscribe({
      next: (items: ProcedureTypeItem[]) => {
        this.procedureTypeOptions = items;
      },
      error: () => {
        this.procedureTypeOptions = [];
      },
    });
  }

  private isUserDetailItem(item: UserItem): item is UserDetailItem {
    return 'clinic' in item && 'specialistProcedureTypes' in item;
  }

  protected clientRatingEventLabel(event: UserClientRatingEvent): string {
    switch (event.eventType) {
      case 'late_cancel':
        return 'clientReliability.event.lateCancel';
      case 'no_show':
        return 'clientReliability.event.noShow';
      case 'late_arrival':
        return 'clientReliability.event.lateArrival';
      case 'client_reschedule':
        return 'clientReliability.event.clientReschedule';
      case 'attended_on_time':
        return 'clientReliability.event.attendedOnTime';
      case 'manual_adjustment':
        return 'clientReliability.event.manualAdjustment';
      default:
        return event.eventType;
    }
  }

  protected clientRatingEventMeta(event: UserClientRatingEvent): number | null {
    if (event.eventType !== 'client_reschedule') {
      return null;
    }

    const rawStep = event.payloadJson?.['churnStep'];
    const churnStep = typeof rawStep === 'number'
      ? rawStep
      : typeof rawStep === 'string'
        ? Number(rawStep)
        : NaN;

    if (!Number.isFinite(churnStep) || churnStep <= 0) {
      return null;
    }

    return Math.trunc(churnStep);
  }

  private withTenantPath(segments: Array<string>, forcedSlug?: string): Array<string>
  {
    if (forcedSlug) {
      return ['/', forcedSlug, ...segments];
    }

    const role = this.authService.getCurrentRole();
    const slug = this.authService.getPreferredCompanySlug();

    if (role !== 'system_admin' && slug)
    {
      return ['/', slug, ...segments];
    }

    return ['/', ...segments];
  }

  private normalizeBirthDateForInput(value: string | null): string
  {
    if (!value) {
      return '';
    }

    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    const year = parsed.getUTCFullYear();
    const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
    const day = String(parsed.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private normalizeIdList(items: string[] | null | undefined): string[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return Array.from(
      new Set(
        items
          .map((item) => item.trim())
          .filter((item) => !!item),
      ),
    );
  }

  private normalizeLabel(value: string | null | undefined, fallback: string): string {
    const normalized = (value ?? '').trim().replace(/\s+/g, ' ');
    return normalized || fallback;
  }

  private isAllowedRole(role: UserRole | null): boolean {
    return role === 'system_admin' || role === 'company_admin' || role === 'manager';
  }

  private resolveTelegramError(error: unknown): string {
    return resolveHttpErrorMessageKey(error, {
      fallback: 'error.generic',
      forbidden: 'error.forbiddenResource',
      badRequestByErrorCode: {
        telegram_webhook_not_configured: 'telegram.error.webhookNotConfigured',
      },
    });
  }
}
